/* Search for blogs/RSS feeds via Feedly, preview articles, and subscribe via sources.create. */

import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Search,
  FileText,
  Star,
  Globe,
  Users,
  Zap,
  ExternalLink,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { useBlogSearch } from "@/hooks/useBlogSearch";
import { SourceTagSelect } from "@/components/sources/SourceTagSelect";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";
import type { BlogSearchResult, BlogArticlePreview, PopularBlog } from "@/types/blogSearch";

const CATEGORY_OPTIONS = [
  { value: "technology", label: "Technology" },
  { value: "programming", label: "Programming" },
  { value: "ai", label: "AI / ML" },
  { value: "design", label: "Design" },
  { value: "business", label: "Business" },
  { value: "news", label: "News" },
  { value: "science", label: "Science" },
  { value: "marketing", label: "Marketing" },
  { value: "finance", label: "Finance" },
  { value: "health", label: "Health" },
  { value: "productivity", label: "Productivity" },
  { value: "security", label: "Security" },
] as const;

interface BlogSearchFormProps {
  onSuccess: () => void;
  footerContainer?: HTMLElement | null;
}

export function BlogSearchForm({ onSuccess, footerContainer }: BlogSearchFormProps) {
  const { results, isSearching, error: searchError, search } = useBlogSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<BlogSearchResult | null>(null);
  const [articles, setArticles] = useState<BlogArticlePreview[]>([]);
  const [isFetchingArticles, setIsFetchingArticles] = useState(false);
  const [articlesError, setArticlesError] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [starred, setStarred] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [popularBlogs, setPopularBlogs] = useState<PopularBlog[]>([]);
  const [isFetchingPopular, setIsFetchingPopular] = useState(false);
  const [popularCategory, setPopularCategory] = useState("technology");
  const [showPopular, setShowPopular] = useState(false);

  const createSource = useMutation(api.sources.create);
  const fetchArticlesAction = useAction(api.blogSearch.fetchBlogArticles);
  const fetchPopularAction = useAction(api.blogSearch.fetchPopularBlogs);
  const availableTags = useQuery(api.tags.getAvailableTags) ?? [];
  const addTag = useMutation(api.tags.addTag);
  const portalTargetRef = useRef<HTMLDivElement>(null);

  const loadArticles = useCallback(async (feedUrl: string) => {
    setIsFetchingArticles(true);
    setArticlesError(null);
    setArticles([]);
    try {
      const data = await fetchArticlesAction({ feedUrl });
      setArticles(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load articles";
      setArticlesError(message);
    } finally {
      setIsFetchingArticles(false);
    }
  }, [fetchArticlesAction]);

  const loadPopular = useCallback(async (category: string) => {
    setIsFetchingPopular(true);
    try {
      const data = await fetchPopularAction({ category, limit: 25 });
      setPopularBlogs(data);
    } catch {
      setPopularBlogs([]);
    } finally {
      setIsFetchingPopular(false);
    }
  }, [fetchPopularAction]);

  function handleTogglePopular() {
    if (!showPopular) {
      setShowPopular(true);
      setSelected(null);
      setArticles([]);
      loadPopular(popularCategory);
    } else {
      setShowPopular(false);
    }
  }

  function handleCategoryChange(category: string) {
    setPopularCategory(category);
    loadPopular(category);
  }

  function handleSelectPopularBlog(blog: PopularBlog) {
    setShowPopular(false);
    // Convert PopularBlog to BlogSearchResult for selection
    const asBlogResult: BlogSearchResult = {
      feedId: blog.feedId,
      title: blog.title,
      website: blog.website,
      description: blog.description,
      subscribers: blog.subscribers,
      velocity: 0,
      coverUrl: "",
      iconUrl: blog.iconUrl,
      visualUrl: "",
      language: "",
      topics: [blog.category],
      feedUrl: blog.feedUrl,
      lastUpdated: 0,
    };
    handleSelectBlog(asBlogResult);
  }

  function handleSearch() {
    search(searchQuery);
    setSelected(null);
    setArticles([]);
    setArticlesError(null);
    setShowPopular(false);
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  }

  function handleSelectBlog(blog: BlogSearchResult) {
    setSelected(blog);
    setSubmitError(null);
    setArticles([]);
    setArticlesError(null);
    if (blog.feedUrl) {
      loadArticles(blog.feedUrl);
    }
  }

  async function handleCreateTag(tag: string) {
    await addTag({ tag });
  }

  const hasFeedUrl = selected?.feedUrl != null && selected.feedUrl.length > 0;
  const canSubmit = selected !== null && hasFeedUrl && !isSubmitting;

  async function handleSubmit() {
    if (!selected || !selected.feedUrl) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createSource({
        name: selected.title || selected.website,
        type: "rss",
        url: selected.feedUrl,
        tags: tags.length > 0 ? tags : undefined,
        starred: starred || undefined,
        thumbnail: selected.iconUrl || selected.visualUrl || selected.coverUrl || undefined,
        description: selected.description || undefined,
        subscriberCount: selected.subscribers || undefined,
      });
      posthog.capture(Events.SOURCE_ADDED, {
        sourceType: "rss",
        starred,
        blogName: selected.title,
      });
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to subscribe";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      <div ref={portalTargetRef} className="absolute" />

      {/* Search section */}
      <div className="space-y-2">
        <Label>Search Blogs & RSS Feeds</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Search by blog name or topic..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
          />
          <Button
            variant="outline"
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
          >
            {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>
        {searchError && <p className="text-destructive text-sm">{searchError}</p>}
      </div>

      {/* Browse Popular toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleTogglePopular}
        className="w-full"
      >
        <TrendingUp className="size-4" />
        {showPopular ? "Hide Popular Blogs" : "Browse Popular Blogs"}
      </Button>

      {/* Popular blogs section */}
      {showPopular && (
        <div className="space-y-3">
          {/* Category filter pills */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
            {CATEGORY_OPTIONS.map((cat) => (
              <button
                key={cat.value}
                type="button"
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs transition-colors ${
                  popularCategory === cat.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                onClick={() => handleCategoryChange(cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Loading */}
          {isFetchingPopular && (
            <div className="flex items-center gap-2 py-4 justify-center">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Loading popular blogs...</span>
            </div>
          )}

          {/* Popular blogs grid */}
          {!isFetchingPopular && popularBlogs.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {popularBlogs.map((blog, i) => (
                <button
                  key={blog.feedId}
                  type="button"
                  className="flex flex-col items-center gap-1 p-2 rounded-md border border-border cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleSelectPopularBlog(blog)}
                >
                  <div className="relative">
                    {blog.iconUrl ? (
                      <img
                        src={blog.iconUrl}
                        alt={blog.title}
                        className="size-10 rounded-md object-cover bg-muted"
                      />
                    ) : (
                      <div className="size-10 rounded-md bg-muted flex items-center justify-center">
                        <FileText className="size-4 text-muted-foreground/50" />
                      </div>
                    )}
                    <span className="absolute -top-1 -left-1 size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                    {blog.title}
                  </span>
                  {blog.subscribers > 0 && (
                    <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                      {formatSubscribers(blog.subscribers)} readers
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {!isFetchingPopular && popularBlogs.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-3">No results found for this category.</p>
          )}
        </div>
      )}

      {/* Search results — horizontal scroll of blog cards */}
      {results.length > 0 && !showPopular && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {results.map((blog) => (
            <button
              key={blog.feedId}
              type="button"
              className={`flex flex-col items-center gap-1 p-2 rounded-md border cursor-pointer hover:bg-muted/50 shrink-0 w-[100px] ${
                selected?.feedId === blog.feedId ? "border-primary bg-muted/50" : "border-border"
              }`}
              onClick={() => handleSelectBlog(blog)}
            >
              {blog.iconUrl ? (
                <img
                  src={blog.iconUrl}
                  alt={blog.title}
                  className="size-12 rounded-md object-cover bg-muted"
                />
              ) : (
                <div className="size-12 rounded-md bg-muted flex items-center justify-center">
                  <FileText className="size-5 text-muted-foreground/50" />
                </div>
              )}
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {blog.title}
              </span>
              {blog.subscribers > 0 && (
                <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                  {formatSubscribers(blog.subscribers)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Selected blog detail card */}
      {selected && (
        <SelectedBlogDetail
          selected={selected}
          articles={articles}
          isFetchingArticles={isFetchingArticles}
          articlesError={articlesError}
          hasFeedUrl={hasFeedUrl}
          starred={starred}
          onStarredChange={setStarred}
          tags={tags}
          onTagsChange={setTags}
          availableTags={availableTags}
          onCreateTag={handleCreateTag}
          portalTargetRef={portalTargetRef}
        />
      )}

      {submitError && <p className="text-destructive text-sm">{submitError}</p>}

      {footerContainer ? createPortal(
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          {selected && !hasFeedUrl ? "No RSS Feed Found" : "Subscribe"}
        </Button>,
        footerContainer,
      ) : (
        <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          {selected && !hasFeedUrl ? "No RSS Feed Found" : "Subscribe"}
        </Button>
      )}
    </div>
  );
}

// ─── Selected blog detail sub-component ───

interface SelectedBlogDetailProps {
  selected: BlogSearchResult;
  articles: BlogArticlePreview[];
  isFetchingArticles: boolean;
  articlesError: string | null;
  hasFeedUrl: boolean;
  starred: boolean;
  onStarredChange: (starred: boolean) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: string[];
  onCreateTag: (tag: string) => Promise<void>;
  portalTargetRef: React.RefObject<HTMLDivElement | null>;
}

function SelectedBlogDetail({
  selected,
  articles,
  isFetchingArticles,
  articlesError,
  hasFeedUrl,
  starred,
  onStarredChange,
  tags,
  onTagsChange,
  availableTags,
  onCreateTag,
  portalTargetRef,
}: SelectedBlogDetailProps) {
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      {/* Header: icon + basic info */}
      <div className="flex gap-3">
        {selected.iconUrl || selected.visualUrl ? (
          <img
            src={selected.visualUrl || selected.iconUrl}
            alt={selected.title}
            className="size-16 rounded-md object-cover bg-muted shrink-0"
          />
        ) : (
          <div className="size-16 rounded-md bg-muted flex items-center justify-center shrink-0">
            <FileText className="size-6 text-muted-foreground/50" />
          </div>
        )}
        <div className="flex flex-col gap-1.5 min-w-0">
          <p className="text-sm font-semibold leading-tight">{selected.title}</p>
          {selected.website && (
            <a
              href={selected.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-0.5"
            >
              <Globe className="size-3" />
              {selected.website.replace(/^https?:\/\/(www\.)?/, "")}
            </a>
          )}
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            {selected.subscribers > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Users className="size-3" />
                {formatSubscribers(selected.subscribers)} readers
              </span>
            )}
            {selected.velocity > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Zap className="size-3" />
                {selected.velocity.toFixed(1)}/wk
              </span>
            )}
            {selected.language && (
              <span className="inline-flex items-center gap-0.5">
                <Globe className="size-3" />
                {selected.language.toUpperCase()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {selected.description && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
          {selected.description}
        </p>
      )}

      {/* Topics */}
      {selected.topics.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.topics.map((topic) => (
            <Badge key={topic} variant="secondary" className="text-[10px] px-1.5 py-0">
              {topic}
            </Badge>
          ))}
        </div>
      )}

      {/* Feed details */}
      <div className="text-xs space-y-1 border-t border-border pt-2">
        <DetailRow label="Feed URL" value={selected.feedUrl} />
        {selected.website && <DetailRow label="Website" value={selected.website} isUrl />}
        {selected.lastUpdated > 0 && (
          <DetailRow
            label="Last Updated"
            value={new Date(selected.lastUpdated).toLocaleDateString()}
          />
        )}
      </div>

      {/* Article preview */}
      {hasFeedUrl && (
        <div className="space-y-2 border-t border-border pt-2">
          <p className="text-xs font-medium text-muted-foreground">
            Recent Articles {articles.length > 0 && `(${articles.length})`}
          </p>
          {isFetchingArticles && (
            <div className="flex items-center gap-2 py-3 justify-center">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Loading articles...</span>
            </div>
          )}
          {articlesError && (
            <p className="text-destructive text-xs">{articlesError}</p>
          )}
          {articles.length > 0 && (
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {articles.map((article, i) => (
                <ArticleRow key={`${article.link}-${i}`} article={article} />
              ))}
            </div>
          )}
        </div>
      )}

      {!hasFeedUrl && (
        <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1.5">
          No RSS feed URL found for this blog. It can't be subscribed to via FeedKiwi.
        </p>
      )}

      {/* Tags & Star */}
      {hasFeedUrl && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onStarredChange(!starred)}
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <Star className={`size-5 ${starred ? "fill-current text-foreground" : ""}`} />
            </button>
            <span className="text-xs text-muted-foreground">
              {starred ? "Starred — new articles go to queue" : "Star to prioritize"}
            </span>
          </div>

          <div className="space-y-2">
            <Label>Tags</Label>
            <SourceTagSelect
              selectedTags={tags}
              onTagsChange={onTagsChange}
              availableTags={availableTags}
              onCreateTag={onCreateTag}
              portalContainer={portalTargetRef}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Article preview row ───

function ArticleRow({ article }: { article: BlogArticlePreview }) {
  return (
    <div className="flex gap-2 p-2 rounded-md border border-border">
      {article.thumbnail ? (
        <img
          src={article.thumbnail}
          alt={article.title}
          className="size-10 rounded-sm object-cover bg-muted shrink-0"
        />
      ) : (
        <div className="size-10 rounded-sm bg-muted flex items-center justify-center shrink-0">
          <FileText className="size-4 text-muted-foreground/50" />
        </div>
      )}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <p className="text-xs font-medium leading-tight line-clamp-1">{article.title}</p>
        <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
          {article.publishedAt > 0 && (
            <span className="inline-flex items-center gap-0.5">
              <Calendar className="size-2.5" />
              {new Date(article.publishedAt).toLocaleDateString()}
            </span>
          )}
          {article.link && (
            <a
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-primary hover:underline"
            >
              <ExternalLink className="size-2.5" />
              view
            </a>
          )}
        </div>
        {article.description && (
          <p className="text-[10px] text-muted-foreground line-clamp-2 leading-tight">
            {article.description}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Detail row ───

function DetailRow({ label, value, isUrl }: { label: string; value: string; isUrl?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground shrink-0 w-28">{label}</span>
      {isUrl ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary truncate inline-flex items-center gap-0.5 hover:underline"
        >
          <ExternalLink className="size-3 shrink-0" />
          <span className="truncate">{value}</span>
        </a>
      ) : (
        <span className="truncate">{value}</span>
      )}
    </div>
  );
}

// ─── Helpers ───

function formatSubscribers(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}
